/**
 * utils/numberHelpers.ts
 * General number formatting and parsing utilities
 *
 * Provides number formatting functions that are reused across the application.
 * Used by moneyHelpers.ts and other modules for consistent number display.
 */

/**
 * Format a number as a percentage
 *
 * @param value - Decimal value (e.g., 0.015 for 1.5%)
 * @param decimalPlaces - Number of decimal places to show (default: 2)
 * @returns Formatted percentage string (e.g., "1.50%")
 *
 * @example
 * formatPercentage(0.015) // Returns: "1.50%"
 * formatPercentage(0.015, 3) // Returns: "1.500%"
 * formatPercentage(1.5) // Returns: "150.00%" (assumes already in percentage form)
 */
export function formatPercentage(value: number, decimalPlaces: number = 2): string {
  if (!Number.isFinite(value)) {
    return "N/A";
  }

  const percentage = value * 100;
  return `${percentage.toFixed(decimalPlaces)}%`;
}

/**
 * Parse percentage string to decimal
 * Handles various input formats with or without % symbol
 *
 * @param input - String input (e.g., "1.5%", "1.5", "0.015")
 * @returns Decimal number or null if parsing fails
 *
 * @example
 * parsePercentage("1.5%") // Returns: 0.015
 * parsePercentage("1.5") // Returns: 0.015 (assumes percentage form)
 * parsePercentage("150") // Returns: 1.5
 * parsePercentage("invalid") // Returns: null
 */
export function parsePercentage(input: string): number | null {
  if (!input || typeof input !== 'string') {
    return null;
  }

  // Remove whitespace and % symbol
  const cleaned = input.trim().replace(/%/g, '');

  const parsed = parseFloat(cleaned);

  if (!Number.isFinite(parsed)) {
    return null;
  }

  // Convert from percentage to decimal
  return parsed / 100;
}

/**
 * Format a number with specified decimal places
 *
 * @param value - Number to format
 * @param decimalPlaces - Number of decimal places (default: 2)
 * @param locale - Locale for formatting (default: 'en-US')
 * @returns Formatted number string
 *
 * @example
 * formatNumber(1234.5678) // Returns: "1,234.57"
 * formatNumber(1234.5678, 3) // Returns: "1,234.568"
 * formatNumber(1234.5678, 0) // Returns: "1,235"
 */
export function formatNumber(
  value: number,
  decimalPlaces: number = 2,
  locale: string = 'en-US'
): string {
  if (!Number.isFinite(value)) {
    return "N/A";
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  }).format(value);
}

/**
 * Format percentage change with + or - prefix
 *
 * @param percentageChange - Change as decimal (e.g., 0.1 for 10% increase)
 * @param decimalPlaces - Number of decimal places to show (default: 2)
 * @returns Formatted string with + or - prefix
 *
 * @example
 * formatPercentageChange(0.1) // Returns: "+10.00%"
 * formatPercentageChange(-0.05) // Returns: "-5.00%"
 * formatPercentageChange(0.1234, 1) // Returns: "+12.3%"
 */
export function formatPercentageChange(
  percentageChange: number,
  decimalPlaces: number = 2
): string {
  if (!Number.isFinite(percentageChange)) {
    return "N/A";
  }

  const percentage = percentageChange * 100;
  const sign = percentage >= 0 ? '+' : '';

  return `${sign}${percentage.toFixed(decimalPlaces)}%`;
}

/**
 * Clamp a number between min and max values
 *
 * @param value - Value to clamp
 * @param min - Minimum value
 * @param max - Maximum value
 * @returns Clamped value
 *
 * @example
 * clamp(5, 0, 10) // Returns: 5
 * clamp(-5, 0, 10) // Returns: 0
 * clamp(15, 0, 10) // Returns: 10
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Round number to specified decimal places
 *
 * @param value - Number to round
 * @param decimalPlaces - Number of decimal places
 * @returns Rounded number
 *
 * @example
 * roundToDecimal(1.2345, 2) // Returns: 1.23
 * roundToDecimal(1.2355, 2) // Returns: 1.24
 */
export function roundToDecimal(value: number, decimalPlaces: number): number {
  const multiplier = Math.pow(10, decimalPlaces);
  return Math.round(value * multiplier) / multiplier;
}
